import React, { FC, useState, useEffect } from 'react';
import { Carousel as ReactResponsiveCarousel } from 'react-responsive-carousel';
import HeroSectionCarouselArrow from './heroSectionCarouselArrow';
import HeroSectionCarouselIndicator from './HeroSectionCarouselIndicator';
import { HeroSectionCarouselModel } from '../../models/heroSectionCarousel.model';
import { THeroSectionTextProps } from '../../types/heroSectionText.types';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import CustomCarouselDots from '../../../@shared/customCarouselDots';
import Picture from '../../../@shared/picture';
import { formatComponentNumber, googleClickSlideEvent } from '../../../../utils/analytics';

const HeroSectionCarousel: FC<THeroSectionTextProps> = (props) => {
  const interval = 10000;
  const model = new HeroSectionCarouselModel(props);
  const [currentSlide, _setCurrentSlide] = useState(0);
  const hasMultipleSlides = model.slides?.length > 1;
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const setCurrentSlide = hasMultipleSlides
    ? (index: number) => {
        if (currentSlide !== index) {
          _setCurrentSlide(index);
        }
      }
    : () => {
        // Do nothing
      };

  const goToPreviousSlide = () => setCurrentSlide(currentSlide - 1);
  const goToNextSlide = () => setCurrentSlide(currentSlide + 1);
  const [isClickedOnArrow, setIsClickedOnArrow] = useState(false);

  useEffect(() => {
    if (!isClickedOnArrow) {
      return;
    }
    setIsClickedOnArrow(false);
    googleClickSlideEvent(`Slide ${formatComponentNumber(currentSlide + 1)}`, props?.fields?.apiData?.title, currentSlide + 1);
  }, [currentSlide, isClickedOnArrow, props]);

  return (
    <div className="product-hero-section-carousel">
      <ReactResponsiveCarousel
        infiniteLoop={true}
        interval={interval}
        autoPlay={true}
        onChange={setCurrentSlide}
        selectedItem={currentSlide}
        showThumbs={false}
        showArrows={false}
        showIndicators={false}
        showStatus={false}
        stopOnHover={false}
        swipeable={!isDesktop}
        emulateTouch={!isDesktop}
      >
        {model.slides?.map((slide, index) => {
          return (
            <div key={`product-hero-section-carousel-${index}`}>
              <Picture
                productImageProps={{
                  desktopRendition: slide.rendition_desktop_size1200,
                  mobileRendition: slide.rendition_mobile_size500,
                  alt: slide?.alt,
                  pictureClassName: 'product-hero-section-carousel__fixed-aspect-ratio',
                  prioritize: index === 0,
                }}
              />
            </div>
          );
        })}
      </ReactResponsiveCarousel>
      {hasMultipleSlides && (
        <>
          <HeroSectionCarouselArrow
            direction={'previous'}
            onClick={() => {
              goToPreviousSlide();
              setIsClickedOnArrow(true);
            }}
          />
          <ul className="product-hero-section-carousel__controls">
            {(model.slides || []).length > 5 ? (
              <CustomCarouselDots cardsCount={(model.slides || []).length} activeCard={currentSlide + 1} hasAnimation={true} />
            ) : (
              model.slides?.map((_, index) => (
                <HeroSectionCarouselIndicator
                  baseClassName={'product-hero-section-carousel__controls__dots__item'}
                  index={index}
                  key={index}
                  isSelected={index === currentSlide}
                />
              ))
            )}
          </ul>
          <HeroSectionCarouselArrow
            direction={'next'}
            onClick={() => {
              goToNextSlide();
              setIsClickedOnArrow(true);
            }}
          />
        </>
      )}
    </div>
  );
};

export default HeroSectionCarousel;
